import React from 'react';
import { Modal, ModalDialog, Typography, Button, Stack, Checkbox, FormControl, FormLabel, Box, IconButton } from '@mui/joy';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

interface EnrollmentTutorialModalProps {
  open: boolean;
  onClose: () => void;
}

const tutorialContent = [
  {
    icon: SchoolRoundedIcon,
    title: "Welcome to Academix!",
    content: "Let's walk through how to enroll in exams on our platform. This quick tutorial will show you everything you need to know to get started.",
  },
  {
    icon: SearchRoundedIcon,
    title: "Finding Exams",
    content: "Browse available exams on your dashboard. Use filters to search by subject or exam name. Each exam card shows important details like date, time, and duration.",
  },
  {
    icon: TimerRoundedIcon,
    title: "Enrollment Process",
    content: "To enroll in an exam:\n1. Click an exam with 'Enroll Now' status\n2. Review exam details on the checkout page\n3. Confirm you have sufficient quota\n4. Click 'Enroll' to complete registration",
  },
  {
    icon: CheckCircleRoundedIcon,
    title: "After Enrollment",
    content: "Once enrolled:\n• Exam status changes to 'Stand By'\n• Access exam materials when the exam begins\n• Submit your answers within the time limit",
  },
];

const EnrollmentTutorialModal = ({ open, onClose }: EnrollmentTutorialModalProps) => {
  const [dontShowAgain, setDontShowAgain] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(0);
  const totalPages = tutorialContent.length;

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('academix-enrollment-tutorial-shown', 'true');
    }
    onClose();
  };

  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const CurrentIcon = tutorialContent[currentPage].icon;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <ModalDialog
        variant="outlined"
        role="alertdialog"
        sx={{
          maxWidth: 500,
          borderRadius: 'md',
          p: 3,
          boxShadow: 'lg',
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
        }}
      >
        <Stack spacing={2} alignItems="center">
          {/* Progress indicator */}
          <Stack 
            direction="row" 
            spacing={1} 
            sx={{ 
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {tutorialContent.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === currentPage ? 'primary.500' : 'neutral.300',
                  transition: 'background-color 0.3s ease',
                }}
              />
            ))}
          </Stack>

          {/* Icon */}
          <CurrentIcon sx={{ fontSize: 48, color: 'primary.500', mt: 3 }} />

          {/* Content */}
          <Typography level="h2" fontSize="xl2" sx={{ mb: 0.5, textAlign: 'center' }}>
            {tutorialContent[currentPage].title}
          </Typography>

          <Typography textColor="text.secondary" textAlign="center" sx={{ whiteSpace: 'pre-line' }}>
            {tutorialContent[currentPage].content}
          </Typography>

          {/* Navigation buttons */}
          <Stack 
            direction="row" 
            spacing={2} 
            sx={{ 
              mt: 2,
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <IconButton
              variant="outlined"
              color="neutral"
              onClick={prevPage}
              disabled={currentPage === 0}
            >
              <ArrowBackRoundedIcon />
            </IconButton>

            {currentPage === totalPages - 1 ? (
              <Button
                variant="solid"
                color="primary"
                onClick={handleClose}
              >
                Get Started
              </Button>
            ) : (
              <IconButton
                variant="solid"
                color="primary"
                onClick={nextPage}
              >
                <ArrowForwardRoundedIcon />
              </IconButton>
            )}
          </Stack>

          {/* Don't show again checkbox */}
          <FormControl
            orientation="horizontal"
            sx={{ alignItems: 'center', gap: 1 }}
          >
            <Checkbox
              size="sm"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
            <FormLabel>Don't show this again</FormLabel>
          </FormControl>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default EnrollmentTutorialModal;
